import { Injectable, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, EMPTY, from, Observable } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
import { FBUser } from '../shared/models/models';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import {
  Auth,
  authState,
  user,
  signOut,
  sendPasswordResetEmail,
} from '@angular/fire/auth';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(
    private router: Router,
    private toastService: ToastService,
    private auth: Auth,
    private firestore: Firestore,
    private functions: Functions,
    private zone: NgZone,
    private injector: Injector,
  ) {
    // 1. subscrive to the user from the authsate
    authState(auth)
      .pipe(
        switchMap((user) => {
          // 2. get their user profile
          if (!user) {
            // if the user is null
            // console.log('From AccoutService Constructor: User is null - ', user);
            if (this.currentUser.value !== null) {
              console.log('User appears to have signed out.');
              this.currentUser.next(null);
              this.router.navigate(['/login']);
            }

            return EMPTY; // Return an empty observalbe because we don't really have aything...
          } else {
            return from(user.getIdToken(true)).pipe(
              switchMap((token) => {
                return this.getUserInfo(user.uid).pipe(
                  map((profile: any) => {
                    const userData = profile;
                    const retUser: FBUser = {
                      uid: user.uid,
                      token: token,
                      authToken: userData.authToken,
                      displayName: user.displayName,
                      firstName: userData.firstName,
                      lastName: userData.lastName,
                      email: userData.email,
                      roles: userData.roles,
                      workflowStage: userData.workflowStage,
                      couplet: userData.couplet,
                      prod: userData.prod ? true : false,
                      emailVerified: user.emailVerified,
                      teams: userData.teams ? true : false,
                      defaultPaymentMethod: userData.defaultPaymentMethod,
                      teamsCount: userData.teamsCount,
                      maxTeams: userData.maxTeams,
                      activePlans: userData.activePlans,
                      activeSubscriptions: userData.activeSubscriptions,
                      trialEligible:
                        userData.trialEligible === undefined ||
                        userData.trialEligible === true
                          ? true
                          : false,
                      showDashTutorial:
                        userData.showDashTutorial === undefined ||
                        userData.showDashTutorial === true
                          ? true
                          : false,
                      showTeamsTutorial:
                        userData.showTeamsTutorial === undefined ||
                        userData.showTeamsTutorial === true
                          ? true
                          : false,
                      numViewerTeams: userData.numViewerTeams,
                      coach: userData.coach,
                      mpactCodeBuyer: userData.mpactCodeBuyer,
                      partnerId: userData.partnerId ?? null,
                      favoriteTeamIds: userData.favoriteTeamIds ?? [],
                      hiddenTeamIds: userData.hiddenTeamIds ?? [],
                    };

                    this.token.next(userData.authToken ?? ''); // update auth token observable

                    return retUser; // return the new user object
                  }),
                );
              }),
            );
          }
        }),
      )
      .subscribe((user) => {
        this.currentUser.next(user); // update the current user observable
      });
  }

  // Firebase callable promises can settle outside Angular's zone (the SDK's
  // native async internals escape zone.js), so consumers' .then/await
  // continuations mutate template state without triggering change detection.
  // Re-resolve in the zone so every consumer lands inside Angular — mirrors
  // TeamsService.runInZone for observables.
  private inZone<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      promise.then(
        (value) => this.zone.run(() => resolve(value)),
        (err) => this.zone.run(() => reject(err)),
      );
    });
  }

  // Observable twin of inZone() above — re-enters the zone per emission.
  private runInZone<T>() {
    return (source: Observable<T>): Observable<T> =>
      new Observable<T>((subscriber) => {
        const sub = source.subscribe({
          next: (value) => this.zone.run(() => subscriber.next(value)),
          error: (err) => this.zone.run(() => subscriber.error(err)),
          complete: () => this.zone.run(() => subscriber.complete()),
        });
        return () => sub.unsubscribe();
      });
  }

  MpactButton = {
    text: 'TAKE THE MPACT',
    link: 'take-mpact',
    isroute: true,
  };
  // LoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);
  token: BehaviorSubject<string> = new BehaviorSubject(''); // observable of the user's auth token
  currentUser: BehaviorSubject<FBUser> = new BehaviorSubject(null); // observable of the current user

  redirectUrl: string; // This is for the Auth Router Guard - if this exists, the user is sent here instead of evaluating their workflow stage
  loggingIn: BehaviorSubject<boolean> = new BehaviorSubject(false); // For triggering the login screens loading state when handling an alredy logged in user
  logginMessage: BehaviorSubject<string> = new BehaviorSubject('Logging in'); // For displaying login messages

  getUser(): Observable<FBUser> {
    return user(this.auth).pipe(
      switchMap((user) => {
        if (!user) {
          // throw { message: 'User not logged in.', value: user };
          return EMPTY;
        } else {
          return from(user.getIdToken(true)).pipe(
            switchMap((token) => {
              return this.getUserInfo(user.uid).pipe(
                map((profile) => {
                  const userData = profile;
                  const retUser: FBUser = {
                    uid: user.uid,
                    authToken: userData.authToken,
                    displayName: user.displayName,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    email: userData.email,
                    roles: userData.roles,
                    workflowStage: userData.workflowStage,
                    couplet: userData.couplet,
                    prod: userData.prod ? true : false,
                    teams: userData.teams ? true : false,
                    token: token,
                    mpactCodeBuyer: userData.mpactCodeBuyer,
                    partnerId: userData.partnerId ?? null,
                  };

                  this.token.next(userData.authToken ?? '');
                  this.currentUser.next(retUser);

                  return retUser;
                }),
              );
            }),
          );
        }
      }),
    );
  }

  //
  // Watch for changes to the firebase auth state and handle the logic for a logged in user at different workflow stages
  //
  // async handleLoggedInUser(user: FBUser, redirectUrl?: string) {
  //   console.log('Got this FBUser from currentUser: ', user);
  //   if (!user) return; // Early exit if user is null
  //
  //   this.setLoggingIn(true, 'Logging in');
  //
  //   if (redirectUrl || this.redirectUrl) {
  //     this.handleRedirect(redirectUrl);
  //   } else {
  //     await this.handleWorkflowStages(user);
  //   }
  // }
  //
  // private async handleWorkflowStages(user: FBUser) {
  //   switch (user.workflowStage) {
  //     case 1:
  //       this.setLoggingIn(true, 'Redirecting to payment');
  //       this.router.navigate(['/payment']);
  //       break;
  //     case 2:
  //       await this.handleSurvey(user);
  //       break;
  //     case 3:
  //       this.updateUserAndNavigate(
  //         user,
  //         'Redirecting to dashboard',
  //         '/results/',
  //       );
  //       break;
  //     default:
  //       this.setLoggingIn(false, 'Logging in');
  //       this.toastService.showDanger(
  //         'There was an error automatically redirecting you to where you need to go. Please contact us for help.',
  //       );
  //       break;
  //   }
  // }
  //
  // private async handleSurvey(user: FBUser) {
  //   const surveyStatus = await this.fns
  //     .httpsCallable('getSurveyStatus')({ uid: user.uid })
  //     .toPromise();
  //   const nextStep = this.determineNextStepBasedOnSurvey(surveyStatus);
  //   this.updateUserAndNavigate(user, `Redirecting to ${nextStep}`, `/survey/`);
  // }
  //
  // private determineNextStepBasedOnSurvey(surveyStatus: any): string {
  //   switch (surveyStatus.assessment) {
  //     case 'in-process':
  //     case 'new':
  //       return 'assessment';
  //     case 'processed':
  //     case 'complete':
  //       return 'user information';
  //     default:
  //       return 'logging in';
  //   }
  // }
  //
  // private handleRedirect(redirectUrl?: string) {
  //   const url = redirectUrl || this.redirectUrl;
  //   console.log(
  //     'There is a redirect URL: ',
  //     this.redirectUrl,
  //     ' => ',
  //     redirectUrl,
  //   );
  //   this.setLoggingIn(true, 'Redirecting');
  //   this.router.navigate([url]);
  //   this.redirectUrl = null;
  // }
  //
  // private updateUserAndNavigate(
  //   user: FBUser,
  //   logMessage: string,
  //   basePath: string,
  // ) {
  //   const updateUserRecord = this.fns.httpsCallable('updateUserRecord');
  //   updateUserRecord(null)
  //     .toPromise()
  //     .then(
  //       () => {
  //         this.setLoggingIn(true, logMessage);
  //         this.token.next(user.authToken ?? '');
  //         this.router.navigate([`${basePath}${user.authToken}`]);
  //       },
  //       (err) => {
  //         console.error('Error updating user.', err);
  //         this.setLoggingIn(true, logMessage);
  //         this.token.next(user.authToken ?? '');
  //         this.router.navigate([`${basePath}${user.authToken}`]);
  //       },
  //     );
  // }
  //
  // private setLoggingIn(isLoggingIn: boolean, message: string) {
  //   this.loggingIn.next(isLoggingIn);
  //   this.logginMessage.next(message);
  // }

  //
  // Re-reads the signed-in Firebase user so a just-verified email is seen without a page reload.
  // Firebase caches emailVerified until the user object is reloaded, and currentUser is only
  // rebuilt on sign-in/out, so both are refreshed here. Resolves to whether the email is verified.
  //
  async refreshEmailVerified(): Promise<boolean> {
    const fbUser = this.auth.currentUser;
    if (!fbUser) return false;

    await fbUser.reload();
    if (!fbUser.emailVerified) return false;

    // New ID token so backend checks (rules, functions) also see the verified email.
    await fbUser.getIdToken(true);
    const current = this.currentUser.value;
    if (current && !current.emailVerified) {
      this.zone.run(() => this.currentUser.next({ ...current, emailVerified: true }));
    }
    return true;
  }

  async handleLoggedInUser(user: FBUser, redirectUrl?: string) {
    if (!user) return; // if the current user is null drop everything and get out of here

    this.loggingIn.next(true);
    this.logginMessage.next('Logging in');

    if (this.redirectUrl || redirectUrl) {
      this.handleRedirect(redirectUrl);
    } else if (user.workflowStage == 1) {
      // If the user is registered but hasn't finished checkout send them to payment
      this.logginMessage.next(`Redirecting to payment`);

      this.router.navigate(['/payment']);
      this.loggingIn.next(false);
    } else if (user.workflowStage == 2) {
      // If they haven't finished the survey, send them to their survey link
      this.logginMessage.next(`Checking assessment status`);

      const getSurveyStatusFn = httpsCallable<{ uid: string }, { assessment: string }>(this.functions, 'getSurveyStatus');
      const surveyResult = await getSurveyStatusFn({ uid: user.uid });
      const surveyStatus = surveyResult.data;

      this.zone.run(() => {
        if (
          surveyStatus.assessment === 'in-process' ||
          surveyStatus.assessment === 'new'
        ) {
          this.logginMessage.next(`Redirecting to assessment`);
          this.router.navigate(['/survey/' + user.authToken]);
          this.loggingIn.next(false);
        } else if (
          surveyStatus.assessment === 'processed' ||
          surveyStatus.assessment === 'complete'
        ) {
          this.handleUpdateUserRecord(user);
        } else {
          // Handle missing assessment status
          this.loggingIn.next(false);
          this.logginMessage.next(`Error`);
          this.toastService.showDanger(
            'There was an error automatically redirecting you to where you need to go. Please contact us for help.',
          );
        }
      });
    } else if (user.workflowStage == 3) {
      // If they have finished the survey, send them to their dashboard
      if (!user.couplet) {
        console.log('User record needs updated.');

        this.handleUpdateUserRecord(user);
      } else {
        this.logginMessage.next(`Redirecting to dashboard`);
        this.token.next(user.authToken ?? '');
        this.router.navigate(['/results/' + user.authToken]);
        this.loggingIn.next(false);
      }
    } else {
      // Handle missing workflow stage
      this.loggingIn.next(false);
      this.logginMessage.next(`Logging in`);
      this.toastService.showDanger(
        'There was an error automatically redirecting you to where you need to go. Please contact us for help.',
      );
    }
  }

  private handleRedirect(redirectUrl?: string) {
    // If there is a redirect url the user was sent to the login page by a route guard
    console.log(
      'There is a recirect URL: ',
      this.redirectUrl,
      ' => ',
      redirectUrl,
    );
    this.logginMessage.next(`Redirecting`);

    if (redirectUrl) {
      this.router.navigate([redirectUrl]); // Send them to the redirect url instead of redirecting based on workflowStage
      this.redirectUrl = null; // Reset the redirect url to prevent incorrect redirects
      this.loggingIn.next(false); // Set the logging in state to false
    } else {
      this.router.navigate([this.redirectUrl]); // Send them to the redirect url instead of redirecting based on workflowStage
      this.redirectUrl = null; // Reset the redirect url to prevent incorrect redirects
      this.loggingIn.next(false); // Set the logging in state to false
    }
  }

  private handleUpdateUserRecord(user: FBUser) {
    const updateUserRecordFn = httpsCallable(this.functions, 'updateUserRecord');

    this.logginMessage.next(`Updating user information`);
    updateUserRecordFn(null)
      .then(
        () => {
          this.zone.run(() => {
            this.token.next(user.authToken ?? '');
            this.router.navigate(['/results/' + user.authToken]);
            this.loggingIn.next(false);
          });
        },
        () => {
          console.log('Error updating user.');
          this.zone.run(() => {
            this.token.next(user.authToken ?? '');
            this.router.navigate(['/results/' + user.authToken]);
            this.loggingIn.next(false);
          });
        },
      );
  }

  //
  // logout the user
  //
  logout(loginPage: 'dash' | 'site') {
    signOut(this.auth).then(
      () => {
        this.currentUser.next(null); // update current user - this will also be updated to null by the subscription to the authstate but that takes too long

        console.log('User logged out.');
        this.toastService.showSuccess('User logged out successfullly!');
        // if running in standalone mode in iOS, redirect to results login route (these use the same login component)
        switch (loginPage) {
          case 'dash':
            this.router.navigate(['/dash/login']);
            break;
          case 'site':
            this.router.navigate(['/login']);
            break;
          default:
            this.router.navigate(['/login']);
            break;
        }
      },
      (error) => {
        console.error('Error logging out: ', error);
        this.toastService.showDanger('There was an error logging you out...');
      },
    );
  }

  //
  // Get the current user's user id.
  //
  getUserId() {
    return authState(this.auth).pipe(
      take(1),
      map((user) => {
        if (user) {
          return user.uid;
        } else {
          // throw { message: 'User not logged in.', value: user };
          return '';
        }
      }),
    );
  }

  //
  // Get the current user's information by their user id.
  //
  getUserInfo(userId: string) {
    // Invoked lazily inside the auth pipeline's switchMap — outside an
    // injection context — so wrap to engage AngularFire's zone schedulers;
    // this stream feeds currentUser, which drives most of the app's UI.
    return runInInjectionContext(this.injector, () =>
      docData(doc(this.firestore, `users/${userId}`)),
    );
    // return this.db.collection<FBUser>('users').doc(userId).valueChanges()
  }

  //
  // Determine next action for user
  //
  nextStep(user: FBUser) {
    //Status: 1 = Checkout, 2 = Survey In Progress, 3 = Survey Complete
    if (!user) {
      // if the current user is null drop everything and get out of here
      if (this.router.url === '/take-mpact') {
        this.MpactButton.text = 'CREATE AN ACCOUNT';
        this.MpactButton.link = 'register';
        this.MpactButton.isroute = true;
      } else {
        this.MpactButton.text = 'TAKE THE MPACT';
        this.MpactButton.link = '/take-mpact';
        this.MpactButton.isroute = true;
      }
    } else if (user.workflowStage == 1) {
      // If the user is registered but hasn't finished checkout send them to payment
      this.MpactButton.text = 'CREATE AN ACCOUNT';
      this.MpactButton.link = 'payment';
      this.MpactButton.isroute = true;

      // console.log("MPACT Buttons set to: ", this.MpactButton);
    } else if (user.workflowStage == 2) {
      // If they haven't finished the survey, send them to their survey link
      this.MpactButton.text = 'CONTINUE MPACT';
      this.MpactButton.link = `/survey/${user.authToken}`;
      this.MpactButton.isroute = false;

      // this.getContinueSurveyLink(user.authToken)
      //   .pipe(take(1))
      //   .subscribe((res) => {
      //     this.MpactButton.link = `/survey/${user.authToken}`;
      //   });
      // console.log("MPACT Buttons set to: ", this.MpactButton);
    } else if (user.workflowStage == 3) {
      // If they have finished the survey, send them to their dashboard
      this.token.next(user.authToken ?? '');
      this.MpactButton.text = 'MY DASHBOARD';
      this.MpactButton.link = `/dash/results/${user.authToken}`;
      this.MpactButton.isroute = true;

      // console.log("MPACT Buttons set to: ", this.MpactButton);
    } else {
      // Handle missing workflow stage

      if (this.router.url === '/take-mpact') {
        this.MpactButton.text = 'CREATE AN ACCOUNT';
        this.MpactButton.link = 'register';
        this.MpactButton.isroute = true;
      } else {
        this.MpactButton.text = 'TAKE THE MPACT';
        this.MpactButton.link = '/take-mpact';
        this.MpactButton.isroute = true;
      }

      // console.log("MPACT Buttons set to: ", this.MpactButton);
    }
  }

  // getContinueSurveyLink(authToken: string) {
  //   return this.http.get(
  //     'https://surveyservice.thinkaspirant.com/api/survey/getcontinuationquerystring',
  //     {
  //       withCredentials: true,
  //       headers: {
  //         Accept: 'application/json',
  //         'Content-Type': 'application/json',
  //         Authorization: `Survey-Token ${authToken}`,
  //       },
  //     },
  //   );
  // }

  //
  // Initiate the password reset process for this user
  //
  resetPasswordInit(email: string) {
    return sendPasswordResetEmail(this.auth, email, {
      url: 'http://localhost:4200/login',
    });
  }

  //
  // 30s memo cache for httpsCallable results. Reads return the same
  // Observable inside the TTL window — no duplicate network round-trips
  // when the dashboard re-mounts. Writes call invalidateCallableCache().
  //
  private callableCache = new Map<string, { ts: number; value$: Observable<any> }>();
  private callableTtlMs = 30_000;

  private memoizeCallable<T>(key: string, factory: () => Observable<T>): Observable<T> {
    const existing = this.callableCache.get(key);
    if (existing && Date.now() - existing.ts < this.callableTtlMs) {
      return existing.value$ as Observable<T>;
    }
    const value$ = factory().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.callableCache.set(key, { ts: Date.now(), value$ });
    return value$;
  }

  invalidateCallableCache(prefix?: string) {
    if (!prefix) {
      this.callableCache.clear();
      return;
    }
    for (const key of Array.from(this.callableCache.keys())) {
      if (key.startsWith(prefix)) this.callableCache.delete(key);
    }
  }

  //
  // Lists users payment methods
  //
  listPaymentMethods(userId?: string): Observable<any> {
    const cacheKey = `lstPaymentMethods:${userId ?? 'self'}`;
    return this.memoizeCallable(cacheKey, () => {
      const listPMsFn = httpsCallable(this.functions, 'lstPaymentMethods');
      const call = userId ? listPMsFn({ userId }) : listPMsFn(null);
      return from(call).pipe(map((result) => result.data));
    }).pipe(this.runInZone());
  }

  //
  // Deletes a payment method
  //
  deletePaymentMethod(pmtMethod: string): Observable<any> {
    const deletePmtMthdFn = httpsCallable(this.functions, 'deletePaymentMethod');
    this.invalidateCallableCache('lstPaymentMethods');
    return from(deletePmtMthdFn({ pmtMethod })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Sets a payment method as the customer's default
  //
  updateDefaultPaymentMethod(userId: string, pmtMethod: string): Observable<any> {
    const setDefaultPmtMthdFn = httpsCallable(this.functions, 'setDefaultPaymentMethod');
    this.invalidateCallableCache('lstPaymentMethods');
    return from(setDefaultPmtMthdFn({ userId, pmtMethod })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Lists users subscriptions
  //
  listSubscriptions(userId?: string): Observable<any> {
    const cacheKey = `listSubscriptions:${userId ?? 'self'}`;
    return this.memoizeCallable(cacheKey, () => {
      const listSubsFn = httpsCallable(this.functions, 'listSubscriptions');
      const call = userId ? listSubsFn({ userId }) : listSubsFn(null);
      return from(call).pipe(map((result) => result.data));
    }).pipe(this.runInZone());
  }

  //
  // Lists users invoices
  //
  listInvoices(userId?: string): Observable<any> {
    const cacheKey = `listInvoices:${userId ?? 'self'}`;
    return this.memoizeCallable(cacheKey, () => {
      const listInvsFn = httpsCallable(this.functions, 'listInvoices');
      const call = userId ? listInvsFn({ userId }) : listInvsFn(null);
      return from(call).pipe(map((result) => result.data));
    }).pipe(this.runInZone());
  }

  //
  // Lists one-off purchases (assessments, MPACT codes), which aren't Stripe invoices
  //
  listPurchases(userId?: string): Observable<any> {
    const cacheKey = `listPurchases:${userId ?? 'self'}`;
    return this.memoizeCallable(cacheKey, () => {
      const listPurchasesFn = httpsCallable(this.functions, 'listPurchases');
      const call = userId ? listPurchasesFn({ userId }) : listPurchasesFn(null);
      return from(call).pipe(map((result) => result.data));
    }).pipe(this.runInZone());
  }

  //
  // Create a subscription
  //
  createSubscription(
    userId: string,
    price: string,
    paymentMethod: string,
    coupon?: string,
    trialPeriod?: number,
  ): Observable<any> {
    const createSubFn = httpsCallable(this.functions, 'createSubscription');
    this.invalidateCallableCache('listSubscriptions');
    this.invalidateCallableCache('listInvoices');
    return from(
      createSubFn({
        userId,
        plan: price,
        payment_method: paymentMethod,
        coupon,
        trialPeriod,
      }),
    ).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Cancels a subscription
  //
  cancelSubscription(userId: string, subscriptionId: string): Observable<any> {
    const cancelSubFn = httpsCallable(this.functions, 'cancelSubscription');
    this.invalidateCallableCache('listSubscriptions');
    return from(cancelSubFn({ userId, subscriptionId })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Update a subscription
  //
  updateSubscription(
    subscriptionId: string,
    newPlanId: string,
    updateType: 'upgrade' | 'downgrade' | 'scheduled',
    coupon?: string,
  ): Observable<any> {
    const updateSubFn = httpsCallable(this.functions, 'updateSubscription');
    this.invalidateCallableCache('listSubscriptions');
    return from(
      updateSubFn({
        subscriptionId,
        newPlanId,
        updateType,
        coupon,
      }),
    ).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Preview changes to a subscription
  //
  previewSubUpdates(
    subscriptionId: string,
    newPlanId: string,
    shouldProrate: boolean,
    coupon?: string,
  ): Observable<any> {
    const previewSubFn = httpsCallable(this.functions, 'previewSubUpdate');

    return from(
      previewSubFn({
        subscriptionId,
        newPlanId,
        prorate: shouldProrate,
        coupon,
      }),
    ).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Retrieve upcoming invoice
  //
  upcomingInvoice(subscriptionId: string): Observable<any> {
    const upcomingFn = httpsCallable(this.functions, 'upcomingInvoice');

    return from(upcomingFn({ subscriptionId })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Mark a specific tutorial video as seen
  //
  markTutorialSeen(tutType: 'dash' | 'teams'): Observable<any> {
    const markTutFn = httpsCallable(this.functions, 'markTutorialSeen');

    return from(markTutFn({ tutType })).pipe(map((result) => result.data), this.runInZone());
  }

  //
  // Create a PaymentIntent for a self-serve impact-code batch purchase.
  // Server re-checks the buyer flag and validates the amount against
  // products/mpact-code.basePrice. Returns { stripeIntent, batchId }.
  //
  createMpactCodeOrderIntent(
    quantity: number,
    amount: number,
    groupName?: string,
    prefix?: string,
  ) {
    const callable = httpsCallable(this.functions, 'createMpactCodeOrderIntent');
    return this.inZone(callable({ quantity, amount, groupName: groupName ?? '', prefix: prefix ?? '' }));
  }

  renameMpactBatch(batchId: string, name: string) {
    const callable = httpsCallable<
      { batchId: string; name: string },
      { success: boolean; batchId: string; name: string }
    >(this.functions, 'renameMpactBatch');
    return this.inZone(callable({ batchId, name }));
  }

  sponsorTeamMemberWithCode(
    teamId: string,
    memberEmail: string,
    codeId?: string,
    firstName?: string,
    lastName?: string,
  ) {
    const callable = httpsCallable<
      { teamId: string; memberEmail: string; codeId?: string; firstName?: string; lastName?: string },
      { success: boolean; requestId: string; codeId: string }
    >(this.functions, 'sponsorTeamMemberWithCode');
    return this.inZone(callable({ teamId, memberEmail, codeId, firstName, lastName }));
  }

  getReservedCodeForMe() {
    const callable = httpsCallable<unknown, { code: string | null }>(
      this.functions,
      'getReservedCodeForMe',
    );
    return this.inZone(callable({}));
  }

  getAvailableCodeCount(teamId?: string) {
    return this.inZone(
      httpsCallable<{ teamId?: string }, { available: number }>(this.functions, 'getAvailableCodeCount')({ teamId }),
    );
  }

  reserveCodesForInvites(
    teamId: string,
    members: { email: string; firstName?: string; lastName?: string }[],
  ) {
    return this.inZone(
      httpsCallable<
        { teamId: string; members: { email: string; firstName?: string; lastName?: string }[] },
        { results: { email: string; status: string; reason?: string }[] }
      >(this.functions, 'reserveCodesForInvites')({ teamId, members }),
    );
  }

  //
  // Synchronously finalize an MPACT-code purchase after Stripe confirms the
  // card payment. The server re-verifies the PaymentIntent with Stripe and
  // mints the coupons immediately. Matches the assessment pattern (createIntent
  // → confirmCardPayment → createNewSurvey) so buyers don't wait for the
  // webhook. The webhook stays as a safety net; provisioning is idempotent.
  //
  confirmMpactCodeOrder(intentId: string) {
    const callable = httpsCallable(this.functions, 'confirmMpactCodeOrder');
    return this.inZone(callable({ intentId }));
  }
}
